import { Plus, UploadIcon } from 'lucide-react'
import { useRef } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Card, Input } from 'src/shared/components/ui'
import { Button } from 'src/shared/components/ui/button'
import { ImageUploadSection } from '../../shared/ImageUploadSection'
import { getFileInputId } from '../utils'

interface NewColorSectionProps {
  variantId: number
  color: string
  setColor: (color: string) => void
  imageUrls: string
  setImageUrls: (urls: string) => void
  handleImageUpload: (file: File) => Promise<void>
  isUploading: boolean
  error: string | null
  onSave: () => Promise<void>
  isSaving?: boolean
}

export const NewColorSection = ({
  variantId,
  color,
  setColor,
  imageUrls,
  setImageUrls,
  handleImageUpload,
  isUploading,
  error,
  onSave,
  isSaving = false,
}: NewColorSectionProps) => {
  const fileInputId = getFileInputId(variantId)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const canSave = color.trim() && imageUrls.trim() && !isUploading && !isSaving

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await handleImageUpload(file)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileInputClick = () => {
    document.getElementById(fileInputId)?.click()
  }

  return (
    <Card>
      <Column className="gap-4">
        <Input
          label="Color name"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          placeholder="Enter color name (e.g., Black, Red)"
        />

        <ImageUploadSection
          imageUrls={imageUrls}
          setImageUrls={setImageUrls}
          handleImageUpload={handleImageUpload}
          isUploading={isUploading}
          error={error}
          fileInputId={fileInputId}
          hideUploadButton={true}
        />

        <Row className="justify-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            id={fileInputId}
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={handleFileInputClick}
            disabled={isUploading}
          >
            <UploadIcon className="h-4 w-4 mr-2" />
            {isUploading ? 'Uploading...' : 'Upload image'}
          </Button>
          <Button variant="primary" onClick={onSave} disabled={!canSave}>
            <Plus className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save new color variant'}
          </Button>
        </Row>
      </Column>
    </Card>
  )
}
